import angular from 'angular';
import { prop, compose, neq } from 'micro-fp';

import { LABOR_COST_CALCULATOR_TYPE, WAGE_TYPES } from '../../../constants';
import config from '../../../config';
import { CONVERSATION_TYPE } from '../../../settings/constants';

import editProfileUrl from './edit-profile.html';
import editWorkUrl from './edit-work.html';
import employeeProfileUrl from './employee-profile.html';
import editUnavailabilityUrl from './edit-unavailability.html';
import editNotesUrl from './edit-notes.html';
import editWagesUrl from './edit-wages.html';
import editPersonalUrl from './edit-personal.html';
import editTimeoffUrl from './edit-timeoff.html';
import editUrl from './edit.html';
import missingDetailsUrl from './missing-details.html';

export default function EditUserProfileCtrl(
  Authentication,
  AccountService,
  Calendar,
  Restangular,
  RestUtils,
  TabsControl,
  Upload,
  Users,
  Utils,
  UserUtils,
  AvailabilityUtils,
  Groups,
  IntegrationService,
  PersonaCollection,
  SubscriptionUtils,
  Wages,
  PremiumDiscoveryService,
  EventCollection,
  LeaveService,
  ConversationService,
  sdk,
  $uibModal,
  $state,
  $window,
  $timeout,
  $location,
  $q,
  $scope,
  $stateParams,
  dateFilter,
  toDateFilter,
  displayNumberFilter,
  possessiveFilter,
  toTimeShortFilter,
  focus,
  geo,
  user,
  userExtraData
) {
  'ngInject';

  const controller = this;

  this.USER_STATUSES = sdk('constants').USER_STATUSES;
  this.editProfileUrl = editProfileUrl;
  this.editWorkUrl = editWorkUrl;
  this.employeeProfileUrl = employeeProfileUrl;
  this.editUnavailabilityUrl = editUnavailabilityUrl;
  this.editNotesUrl = editNotesUrl;
  this.editWagesUrl = editWagesUrl;
  this.editPersonalUrl = editPersonalUrl;
  this.editTimeoffUrl = editTimeoffUrl;
  this.editUrl = editUrl;
  this.editUrl = editUrl;
  this.missingDetailsUrl = missingDetailsUrl;
  this.WAGE_TYPES = WAGE_TYPES;
  this.MANAGERS_ASSIGNED_TO_USERS_ENABLED = config.MANAGERS_ASSIGNED_TO_USERS_ENABLED;
  this.SPLIT_USER_PROFILE_ENABLED = config.SPLIT_USER_PROFILE_ENABLED;
  this.resetCounter = 0;
  this.numberWithDecimalPattern = Utils.getNumberValidationPattern();
  this.getOriginalUserCopy = getOriginalUserCopy;
  this.UNAVAILABILITY_LIST_V2_ENABLED = config.UNAVAILABILITY_LIST_V2_ENABLED;
  this.SubscriptionUtils = SubscriptionUtils;
  this.AccountService = AccountService;

  this.getUserFieldTooltip = (field) => {
    if (user.origin && controller.userSharedFields.includes(field)) {
      return `Edit this field on ${user.origin} side first for Sling to reflect any required updates.`;
    } else if (!Authentication.user.isManager && ['name', 'lastname'].includes(field)) {
      return "Contact your manager if you'd like to update this field.";
    }

    return null;
  };

  _initWages();

  controller.conversationTypeNone = CONVERSATION_TYPE.NONE;
  controller.UserUtils = UserUtils;

  controller.company = Authentication.user.org.name;
  // The form needs to be able to access the unchanged email
  controller.user = user;
  controller.originalUser = user;
  controller.originalUserExtraData = userExtraData;
  controller.allUsers = PersonaCollection.users;

  controller.dateForms = new Map();
  controller.rateForms = new Map();

  controller.hasAccessToLaborCost = Authentication.user.hasAccessToLaborCost;
  controller.readOnly = !UserUtils.canEditUser(user);
  controller.wagesReadOnly =
    controller.readOnly || !UserUtils.canEditUserWages(user) || !controller.hasAccessToLaborCost;
  controller.canEditGroups = UserUtils.canEditUser(user) && Authentication.user.isManager;
  controller.canEditLocations = controller.canEditGroups;
  controller.canEditPositions = controller.canEditGroups;

  controller.isManager = Authentication.user.isManager;
  controller.isPremium = Authentication.user.org.isPremium;

  controller.canSeeDescription = 'description' in userExtraData;
  controller.canSeeLeaveCaps = UserUtils.canAccessUserLeaveCaps(user);
  controller.canManageUser = UserUtils.canManageUser(user);
  controller.canEditLeaveCaps = Authentication.user.isAdmin;
  controller.canSeePin =
    UserUtils.canAccessUserPin(user) && Authentication.isOrgFeatureEnabled('kiosk');
  controller.userPin = userExtraData.kioskPin;
  controller.originalUserPin = userExtraData.kioskPin;
  controller.userSharedFields = [];

  controller.userExtraData = getOriginalExtraData();

  controller.uploader = angular.copy(Upload.uploader);
  controller.uploader.autoUpload = false;
  controller.uploader.onAfterAddingFile = function onAfterAddingFile() {
    if (controller.uploader.queue.length > 1) {
      controller.uploader.removeFromQueue(controller.uploader.queue[0]);
    }
  };

  controller.timezones = RestUtils.getDefaultTimezones(controller.originalUser);
  controller.manageMode = Authentication.user.isManager;

  controller.groups = PersonaCollection.groups;
  controller.positions = PersonaCollection.positions;
  controller.locations = PersonaCollection.locations;
  controller.utils = UserUtils;

  controller.availabilitySets = [];
  controller.availabilityLoading = !controller.readOnly;

  Object.defineProperty(controller, 'documentIdFromPath', {
    get() {
      const { documentId } = $location.search();
      if (!documentId) {
        return null;
      }

      return documentId === 'new' ? documentId : Number(documentId);
    },
  });

  controller.setDocumentIdInPath = (documentId) => {
    $scope.$applyAsync(() => {
      $location.search('documentId', documentId);
    });
  };

  controller.tabs = [
    {
      label: 'Personal',
      value: 'personal',
      show() {
        return (
          config.SPLIT_USER_PROFILE_ENABLED &&
          (Authentication.user?.isManager || controller.user?.isMe)
        );
      },
    },
    {
      label: 'Work',
      value: 'work',
      show() {
        return (
          config.SPLIT_USER_PROFILE_ENABLED &&
          (Authentication.user?.isManager || controller.user?.isMe)
        );
      },
    },
    {
      label: 'Profile',
      value: 'profile',
      show() {
        return !config.SPLIT_USER_PROFILE_ENABLED;
      },
    },
    {
      label: 'Profile',
      value: 'otherEmployeeProfile',
      show() {
        return (
          config.SPLIT_USER_PROFILE_ENABLED &&
          !controller.user?.isMe &&
          !Authentication.user?.isManager
        );
      },
    },
    {
      label: 'Documents',
      value: 'documents',
      show() {
        return (
          Authentication.user?.isAdmin ||
          (controller.user?.isMe &&
            (Authentication.isOrgFeatureEnabled('unavailability') ||
              Authentication.user?.isManager)) ||
          (Authentication.user?.isManager &&
            controller.userExtraData?.managers.find((m) => m.id === Authentication.user.id))
        );
      },
    },
    {
      label: 'Unavailability',
      value: 'availability',
      show() {
        const usersSetAvailability = Authentication.user?.orgSettings?.usersSetAvailability;
        const isManager = Authentication.user?.isManager;

        return (
          !controller.readOnly &&
          (controller.availabilityNotEmpty ||
            $stateParams.unavailabilityId ||
            usersSetAvailability ||
            isManager)
        );
      },
    },
    {
      label: 'Wages',
      value: 'wages',
      show() {
        const canSeeWage = Authentication.user.isAdmin || !controller.originalUser.isManager;
        return (
          (Authentication.user.wouldHaveAccessToLaborCost &&
            canSeeWage &&
            controller.canManageUser) ||
          (controller.originalUser.isMe && Authentication.user.canSeeOwnWage)
        );
      },
    },
    {
      label: 'Time Off',
      value: 'timeoff',
      show() {
        return controller.canManageUser;
      },
    },
    {
      label: 'Notes',
      value: 'notes',
      show() {
        return controller.canSeeDescription;
      },
    },
  ];

  controller.availableTabs = controller.tabs.filter((t) => !t.show || t.show());
  controller.tabsControl = new TabsControl(controller.tabs, 'profile');
  controller.changeTab = function changeTab(value) {
    const selectedTab = controller.tabs.find((tab) => tab.value === value);
    if (!selectedTab || controller.tabsControl.activeTab.value === value) {
      return;
    }

    controller.tabsControl.changeTab(selectedTab, true);
  };

  controller.getInvalidTabs = function getInvalidTabs() {
    return controller.tabs
      .map((tab) => tab.value)
      .filter(
        (value) => controller[`${value}Form`]?.$invalid && controller[`${value}Form`]?.$dirty
      );
  };

  controller.roleDict = {
    user: 'Employee',
    manager: 'Manager',
    admin: 'Admin',
  };

  controller.systemRoles = ['manager', 'user'];

  Object.defineProperty(controller, 'currentRoleDisabled', {
    get() {
      if (controller.user.isMe) {
        return true;
      }

      return (
        controller.systemRoles.length < 2 || !controller.systemRoles.includes(controller.user.role)
      );
    },
  });
  async function init() {
    controller.userSession = await UserUtils.getSession();
    controller.userToastIdentityGuid = controller.userSession?.toastIdentityGuid;
    controller.userIsToastAuth0Enabled = controller.userToastIdentityGuid !== null && controller.userToastIdentityGuid !== undefined;
    controller.redirectToToastUpdate = redirectToToastUpdate;

    if (Authentication.user.isAdmin) {
      controller.systemRoles.unshift('admin');
    }
    if (
      !config.UNAVAILABILITY_LIST_V2_ENABLED &&
      !controller.readOnly &&
      Authentication.isOrgFeatureEnabled('unavailability')
    ) {
      AvailabilityUtils.getAvailabilities(controller.originalUser).then((availabilitySets) => {
        controller.availabilitySets = availabilitySets;
        controller.availabilityLoading = false;
        controller.availabilityNotEmpty = availabilitySets.some(
          (set) => Object.keys(set.unavailabilities).length > 0
        );
        AvailabilityUtils.resetAvailabilitySets(controller.availabilitySets);
      });
    }

    IntegrationService.getAvailableIntegrations().then((integrations) => {
      if (!controller.user.origin) {
        return;
      }

      controller.userSharedFields = integrations.find(
        (integration) => integration.type === controller.user.origin
      ).userSharedFields;
      controller.canEditLocations =
        !controller.userSharedFields.includes('locations') && controller.canEditLocations;
      controller.canEditPositions =
        !controller.userSharedFields.includes('positions') && controller.canEditPositions;
      controller.wagesReadOnly =
        controller.userSharedFields.includes('wages') || controller.wagesReadOnly;
    });

    _prepareTimeoffReportYearOptions();
  }

  function redirectToToastUpdate() {
    const tab = window.open(`${config.TOAST_URL}/settings/my-account#`, '_blank');
    tab.focus();
  }

  function resetUser() {
    // Edit a copy, otherwise the data binding shows changes before saving them
    // (which yes, looks cool, but then you have to deal with user navigating away
    // and undoing the changes, etc. Too much work.)
    controller.resetCounter++;
    controller.user = getOriginalUserCopy();
    controller.userExtraData = getOriginalExtraData();
    controller.getCurrentWages();
    controller.error = null;
    if (controller.form) {
      controller.form.$setSubmitted(false);
    }

    if (controller.user.pending) {
      controller.user.oldEmail = angular.copy(controller.user.email);
      controller.user.email = angular.copy(controller.user.pending);
    }

    controller.user.emailCopy = angular.copy(controller.user.email);
    controller.user.currentAvatar = angular.copy(controller.user.avatar);
    controller.availableGroups = UserUtils.getAvailableGroups(controller.user);
    controller.locations = Authentication.user.isAdmin
      ? PersonaCollection.locations
      : PersonaCollection.me.locations;
    controller.positions = PersonaCollection.positions;
    controller.groups = PersonaCollection.groups;

    controller.refreshManagers = () => {
      controller.managers = UserUtils.getPossibleManagersForUser(controller.user);
      controller.nonDeletableManagers = [];

      if (!Authentication.user.isAdmin) {
        controller.nonDeletableManagers =
          controller.userExtraData.managers?.filter((m) => !m.isMe).map((m) => m.id) || [];
      }
    };

    controller.refreshManagers();

    if (controller._originalUserCaps) {
      _parseLeaveCaps(controller._originalUserCaps);
    }
  }

  function getOriginalUserCopy() {
    const copyGroupsFn = controller.canEditGroups
      ? (p) => ({ id: p.id, type: p.type, name: p.name })
      : (p) => ({ id: p.id, type: p.type, name: p.name, fullname: p.fullname, color: p.color });

    return PersonaCollection.getShallowUserCopy(controller.originalUser, copyGroupsFn);
  }

  async function onSaveSuccess() {
    controller.originalUser = PersonaCollection.personasDict[controller.user.id];
    controller.originalUserExtraData = await UserUtils.getExtraUserData(controller.originalUser);
    resetUser();
    _filterEmptyWages();
    controller.form.$setPristine();
    return true;
  }

  function _wageChanged(wage) {
    const editedDateOrRate =
      '_editing' in wage &&
      '_prevFromDate' in wage &&
      (wage._prevFromDate.getTime() !== wage.fromDate.getTime() ||
        wage._prevRegularRate !== wage.regularRate);

    return editedDateOrRate || wage._isSalaryForm?.$dirty;
  }

  async function _saveToServer() {
    const promises = [];
    const isFormDirty = controller.SPLIT_USER_PROFILE_ENABLED
      ? controller.personalForm?.$dirty || controller.workForm?.$dirty
      : controller.profileForm?.$dirty;

    if (isFormDirty) {
      Object.assign(controller.user, controller.userExtraData);
      promises.push(controller.user.put({ 'user-fields': 'full' }));
    }

    if (controller.availabilityForm?.$dirty) {
      promises.push(AvailabilityUtils.saveAvailability(controller.availabilitySets));
    }

    if (controller.shouldSaveUserPin) {
      promises.push(controller.savePin());
    }

    controller.originalUserPin = controller.userPin;

    // user needs to be assigned to positions before we start messing with wages
    await $q.all(promises);

    const wagesToBulkUpdate = controller.getSalariesToBulkUpdate?.() || [];
    controller.user.wagesList.forEach((wageGroup) => {
      const positionId = { wageGroup };
      wageGroup.groupWages
        .filter((w) => w.userId && !w.baseWage)
        .forEach((wage) => {
          if (!wage.positionId) {
            wage.positionId = positionId;
          }

          if (wage.deleted || !('id' in wage) || _wageChanged(wage)) {
            wagesToBulkUpdate.push(wage);
          }
        });
    });

    _addUserBaseWagesToUpdate(wagesToBulkUpdate);

    if (wagesToBulkUpdate.length) {
      await Restangular.all('labor/wages/user/bulk').customPUT(wagesToBulkUpdate);
      PremiumDiscoveryService.featureDiscovered(PremiumDiscoveryService.FEATURE_NAMES.LABOR_COST);
    }

    return onSaveSuccess();
  }

  controller.rolesDescriptions = {
    user: 'Employees can view their schedules, exchange\n shifts and request time off.',
    manager: 'Managers can create and edit schedules and\n employees within their locations.',
    admin:
      'Admins control org settings and can create and\n edit schedules and employees in all locations.',
  };

  controller.goBack = function getBackRoute() {
    if ($window.history.length) {
      return $window.history.back();
    }

    return $state.go(UserUtils.getUsersSref());
  };

  function _deleteOrDeactivate(_user, mode) {
    const onSuccess = (result) => {
      if (!result) {
        return;
      }

      if (mode === 'delete') {
        PersonaCollection.users = PersonaCollection.users.filter((u) => u.id !== _user.id);
        $state.go('dashboard.manage.users.list', null, { reload: true });
      } else {
        controller.originalUser.active = false;
        resetUser();
        controller.form.$setPristine();
      }
    };

    return UserUtils.deleteOrDeactivateUser(_user, mode, onSuccess);
  }

  function _createAnyGroups() {
    const newPositions = controller.user.positions.filter((p) => p.$$new);
    const newLocations = controller.user.locations.filter((l) => l.$$new);
    const newGroups = controller.user.groups.filter((g) => g.$$new);

    const promises = newPositions
      .concat(newLocations)
      .concat(newGroups)
      .map((group) =>
        Groups.withHttpConfig({ ignoreGlobalErrorHandler: true })
          .post(group)
          .then((data) => {
            delete group.$$new;
            angular.extend(group, data);
            return data;
          })
      );

    return $q.all(promises);
  }

  controller.deleteUser = function deleteUser(_user) {
    _deleteOrDeactivate(_user, 'delete');
  };

  controller.deactivateUser = function deactivateUser(_user) {
    _deleteOrDeactivate(_user, 'deactivate');
  };

  controller.resetAvatar = function resetAvatar() {
    if (controller.uploader.queue.length) {
      controller.user.avatar = controller.user.currentAvatar;
      controller.uploader.clearQueue();
    } else {
      controller.user.avatar = controller.user.defaultAvatar;
    }

    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.personalForm.$setDirty();
    } else {
      controller.profileForm.$setDirty();
    }
  };

  controller.submit = async function submit() {
    if (controller.form.$invalid) {
      throw 'invalid form';
    }

    controller.error = null;
    controller.form.$setSubmitted();

    await _createAnyGroups();

    // Upload
    await $q((resolve, reject) => {
      if (controller.uploader.queue.length) {
        controller.uploader.onSuccessItem = function onSuccessItem(fileItem, { url }) {
          if (url && url.indexOf('http') === 0) {
            controller.user.avatar = url;
          }

          resolve();
        };

        controller.uploader.onErrorItem = function onErrorItem() {
          reject();
        };

        controller.uploader.uploadAll();
      } else {
        resolve();
      }
    });

    const changes = Object.keys(userExtraData).reduce(
      (acc, field) => ({
        ...acc,
        [field]: controller?.[`formExtraData-${field}`]?.$dirty,
      }),
      {}
    );

    controller.originalUserExtraData = await UserUtils.updateExtraUserData(
      controller.user,
      controller.userExtraData,
      changes
    );
    const capsChanged = await _saveLeaveCaps();

    Object.keys(userExtraData).forEach((field) => {
      controller?.[`formExtraData-${field}`]?.$setPristine();
    });

    const saveResult = await _saveToServer();

    if (capsChanged && controller.tabsControl.activeTab.value === 'timeoff') {
      _refreshTimeoffReport();
    }

    return saveResult;
  };

  controller.cancel = function cancel() {
    resetUser();

    controller.userExtraData = { ...getOriginalExtraData() };
    Object.keys(userExtraData).forEach((field) => {
      controller?.[`formExtraData-${field}`]?.$setPristine();
    });

    controller.userPin = controller.originalUserPin;
    controller.cancelUnavailabilityEdit();

    if (controller.uploader.queue.length) {
      controller.user.avatar = controller.user.currentAvatar;
      controller.uploader.clearQueue();
    }
    if (controller.form) {
      controller.form.$setPristine();
    }
    if (controller.isDetailsShown) {
      controller.isDetailsShown = false;
    }
  };

  controller.cancelUnavailabilityEdit = function cancelUnavailabilityCreating() {
    if (
      !config.UNAVAILABILITY_LIST_V2_ENABLED ||
      (controller.availabilitySets.length && controller.availabilitySets[0].availabilityId)
    ) {
      AvailabilityUtils.resetAvailabilitySets(controller.availabilitySets);
      return;
    }

    $state.go('.', { newSet: null, unavailabilityId: null, copySet: null });
  };

  controller.activateUser = function activateUser() {
    const userCopy = getOriginalUserCopy();
    userCopy.active = true;
    return userCopy.put({ 'user-fields': 'full' }).then((updatedUser) => {
      controller.originalUser = updatedUser;
      resetUser();
      if (controller.form) {
        controller.form.$setPristine();
      }
      return true;
    });
  };

  controller.reInvite = function reInvite() {
    return Users.all('invite')
      .post([{ id: controller.originalUser.id }])
      .then(() => {
        if (controller.originalUser.status === controller.USER_STATUSES.UNINVITED) {
          controller.originalUser.status = controller.USER_STATUSES.INVITED;
          controller.user.status = controller.USER_STATUSES.INVITED;
        }
      });
  };

  Object.defineProperty(controller, 'isEmailOrPhoneProvided', {
    get() {
      return controller.personalForm.email.$dirty || controller.personalForm.phone.$dirty;
    },
  });

  Object.defineProperty(controller, 'isSendInviteAvailable', {
    get() {
      return (
        controller.user.missingInviteDetails &&
        controller.user.status === controller.USER_STATUSES.UNINVITED
      );
    },
  });

  controller.isInviteManualyDisalbled = false;
  controller.isDetailsShown = false;

  controller.onInviteChange = function onInviteChange(value) {
    controller.isInviteManualyDisalbled = !value;

    controller.user.invite = value;
    controller.personalForm.$setDirty();
    $scope.$digest();
  };

  controller.onEmailChange = function onEmailChange(email, form) {
    if (!controller.isSendInviteAvailable) return;

    if (!controller.isInviteManualyDisalbled && email && form?.$valid) {
      controller.user.invite = true;
    }
  };

  controller.onTimeclockEnabledChange = function onTimeclockEnabledChange(value) {
    controller.user.timeclockEnabled = value;
    controller.personalForm.$setDirty();
    $scope.$digest();
  };

  controller.onHiddenOnSchedule = function onHiddenOnSchedule(value) {
    controller.user.hiddenOnSchedule = value;
    controller.personalForm.$setDirty();
    $scope.$digest();
  };

  let phoneBefore;
  controller.onPhoneChange = function onPhoneChange(phone, form) {
    if (!controller.isSendInviteAvailable) return;

    const wasPhoneRemoved = phoneBefore && !phone;
    phoneBefore = phone;

    if (!!controller.isInviteManualyDisalbled && controller.user.phone && form.$valid) {
      controller.user.invite = true;
      return;
    }
    if (wasPhoneRemoved && !controller.user.phone && !controller.personalForm.email.$viewValue) {
      controller.user.invite = false;
    }
  };

  controller.canSendInvite = function canSendInvite() {
    return (
      (controller.user.email && !controller.personalForm.email.$invalid) ||
      (controller.user.phone && !controller.personalForm.phone.$invalid)
    );
  };

  controller.isPhoneOrEmailRequired = function isPhoneOrEmailRequired() {
    return controller.user.invite && !controller.canSendInvite();
  };

  $scope.$watch('ctrl.personalForm.email.$viewValue', () => {
    if (
      !controller.isInviteManualyDisalbled &&
      !controller.personalForm?.email?.$viewValue &&
      !controller.user.phone
    ) {
      controller.user.invite = false;
    }
  });

  controller.loadTimezones = async function loadTimezones() {
    controller.timezones = await RestUtils.getTimezones();
  };

  controller.deleteBirthDate = function deleteBirthDate() {
    controller.user.birthdayDate = null;
    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.personalForm.$setDirty();
    } else {
      controller.profileForm?.$setDirty();
    }
  };

  controller.deleteHireDate = function deleteHireDate() {
    controller.user.hireDate = null;
    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.workForm.$setDirty();
    } else {
      controller.profileForm?.$setDirty();
    }
  };

  Object.defineProperty(controller, 'pendingEmailText', {
    get() {
      let whose;

      if (controller.user.isMe) {
        whose = 'your';
      } else {
        whose = `${controller.user.name}'`;

        if (!/s$/i.test(controller.user.name)) {
          whose += 's';
        }
      }

      const who = controller.user.isMe ? 'you verify' : `${controller.user.name} verifies`;

      return `${whose} old address ${controller.user.oldEmail} still used by Sling until ${who} ${controller.user.emailCopy}`;
    },
  });

  controller.getCurrentWages = function getCurrentWages() {
    if (controller.wageTypeFilter === WAGE_TYPES.HOURLY) {
      _getCurrentHourlyWages();
    } else {
      controller.refreshCurrentSummaries?.();
    }
  };

  function _getCurrentHourlyWages() {
    const historicalWagesForPosition = (positionId) =>
      controller.user.historicalWagesPerPosition?.[positionId] || [];

    controller.currentHourlyWages = controller.user.wagesList.map((wagesGroup) => ({
      ...wagesGroup,
      ref: wagesGroup,
      groupWages: wagesGroup.groupWages
        .filter((w) => !w.isSalary)
        .filter(
          (w) =>
            !historicalWagesForPosition(wagesGroup.positionId).find(
              (historical) => historical.wage.id === w.id
            )
        ),
    }));
  }

  function _initWages() {
    const salaryPeriod =
      Authentication.user.orgSettings.laborCostCalculatorType === LABOR_COST_CALCULATOR_TYPE.ICELAND
        ? 'Monthly'
        : 'Annual';

    controller.wageTypeFilterOptions = [
      { label: 'Hourly', value: WAGE_TYPES.HOURLY },
      {
        disabled: !Authentication.isOrgFeatureEnabled('salaries'),
        label: `${salaryPeriod} salary`,
        value: WAGE_TYPES.SALARY,
        premiumGuard: 'salaries',
        premiumGuardModal: 'salaries',
        premiumGuardTooltipPlacement: 'left',
      },
    ];

    controller.wageTypeFilter = WAGE_TYPES.HOURLY;

    if (Authentication.isOrgFeatureEnabled('salaries') && user.currentSalary) {
      controller.wageTypeFilter = WAGE_TYPES.SALARY;
    }
  }

  controller.wagesSortMethod = 'position';

  controller.addWageRate = async function addWageRate(wagesGroup, focusId) {
    const wageObject = {
      _editing: true,
      fromDate: UserUtils.getWageDefaultDate(controller.user, wagesGroup.position.id),
      userId: controller.user.id,
      positionId: wagesGroup.position.id,
    };

    wagesGroup.ref.groupWages.push(wageObject);
    controller.form.$setDirty();

    controller.getCurrentWages();

    await $timeout(0);
    focus(focusId);
  };

  controller.editWageRate = async function editWageRate(wage, focusId) {
    wage._editing = !wage._editing;
    wage._prevFromDate = new Date(wage.fromDate);
    wage._prevRegularRate = wage.regularRate;

    controller.form.$setDirty();

    await $timeout(0);
    focus(focusId);
  };

  controller.wageKeyDown = function wageKeyDown(event, wage) {
    if (event.keyCode === 13) {
      wage._submitted = true;

      const dateForm = controller.dateForms.get(wage);
      const rateForm = controller.rateForms.get(wage);
      if (dateForm.$valid && rateForm.$valid) {
        wage._editing = false;
        wage._submitted = false;
      }

      event.stopPropagation();
      event.preventDefault();

      return false;
    }
  };

  controller.deleteWageRate = function deleteWageRate(wagesGroup, wage) {
    wage.deleted = true;
    controller.getCurrentWages();
    controller.form.$setDirty();
  };

  controller.sortBy = function sortBy(sortMethod) {
    const currentMethod = controller.wagesSortMethod;
    if (currentMethod === sortMethod) {
      controller.wagesSortMethod = `-${sortMethod}`;
    } else {
      controller.wagesSortMethod = sortMethod;
    }
  };

  controller.getEmptyWagesLabel = function getEmptyWagesLabel() {
    return controller.user.isMe
      ? 'Add yourself to a position first, to be able to define your wages.'
      : 'Add this employee to a position first, to be able to define their wages.';
  };

  controller.dateFormat = Authentication.user.settings.usTimeFormat ? 'MM/dd/yyyy' : 'dd/MM/yyyy';

  controller.forgotPassword = async function forgotPassword() {
    await Restangular.all('account').all('forgot').post({
      email: Authentication.user.email,
    });

    controller.passwordSuccess = `We have sent a password reset link to ${Authentication.user.email}`;
  };

  controller.regeneratePin = async function regeneratePin() {
    controller.userPin = await UserUtils.regeneratePin(controller.user);

    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.workForm.userPin.$setDirty();
    } else {
      controller.profileForm.userPin.$setDirty();
    }

    controller.shouldSaveUserPin = true;
  };

  controller.savePin = async function savePin() {
    if (controller.shouldSaveUserPin) {
      return Authentication.sendRequest({
        method: 'put',
        path: `kiosks/users/${controller.user.id}/pin`,
        payload: {
          kioskPin: controller.userPin,
        },
      });
    }
  };

  controller.canEditPin = function canEditPin() {
    if (!Authentication.user.isManager) {
      return false;
    }

    const hasCommonLocation = Authentication.user.groups.some((group) =>
      controller.user.locationIds.includes(group.id)
    );
    return (
      controller.user.isMe ||
      Authentication.user.isAdmin ||
      (Authentication.user.isManager && hasCommonLocation && !controller.user.isAdmin)
    );
  };

  controller.canRegeneratePin = function canRegeneratePin() {
    if (controller.user.isMe) {
      return true;
    }
    return controller.canEditPin();
  };

  controller.userPinManuallyChanged = function userPinManuallyChanged() {
    controller.shouldSaveUserPin = Boolean(controller.userPin);
  };

  controller.addGroupToUser = function addGroup(item, type) {
    controller.availableGroups = controller.utils.addGroupToUser(controller.user, item, type);
    if (type === 'positions') {
      _addWageToGroup(item);
    }

    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.workForm.$setDirty();
    } else {
      controller.profileForm.$setDirty();
    }
  };

  controller.addAllGroups = function addAllGroups(type) {
    controller.availableGroups = controller.utils.addAllGroups(controller.user, type);
    if (type === 'positions') {
      controller.user.positions.forEach(_addWageToGroup);
    }

    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.workForm.$setDirty();
    } else {
      controller.profileForm.$setDirty();
    }
  };

  controller.removeGroup = function removeGroup(group, type) {
    controller.availableGroups = controller.utils.removeGroupFromUser(controller.user, group, type);
    if (type === 'positions') {
      _filterEmptyWages();
    }

    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.workForm.$setDirty();
    } else {
      controller.profileForm.$setDirty();
    }
  };

  controller.clearGroups = function clearGroups(type) {
    controller.availableGroups = controller.utils.clearGroups(controller.user, type);
    if (type === 'positions') {
      _filterEmptyWages();
    }

    if (controller.SPLIT_USER_PROFILE_ENABLED) {
      controller.workForm.$setDirty();
    } else {
      controller.profileForm.$setDirty();
    }
  };

  controller.navigateToAnother = function navigateToAnother(userId) {
    const params = {
      userId,
      tab: controller.tabsControl.activeTab?.value,
    };

    $state.go('users.profile', params);
  };

  controller.selectCountryCode = function selectCountryCode({ value, code }) {
    controller.user.countryCode = value;
    controller.user.countryISOCode = code;
  };

  controller.selectEmergencyContactCountryCode = function selectCountryCode({ value, code }) {
    controller.user.emergencyContactCountryCode = value;
    controller.user.emergencyContactCountryISOCode = code;
  };

  controller.showSalaryTooltip = function showSalaryTooltip() {
    return controller.wageTypeFilter === controller.WAGE_TYPES.SALARY;
  };

  controller.wageTypeChanged = function wageTypeChanged() {
    controller.user.wagesList = angular.copy(controller.originalUser.wagesList);
    controller.getCurrentWages();
  };

  controller.loadUserLeaveCaps = async function loadUserLeaveCaps() {
    if (controller.userLeaveCaps) {
      return;
    }

    await controller.loadTimeoffTypes();
    _parseLeaveCaps(await _getUserCaps(user.id));
  };

  controller.loadTimeoffTypes = async function loadTimeoffTypes() {
    if (controller.timeOffTypes) {
      return;
    }

    await LeaveService.loadTimeOffTypes();
    controller.timeOffTypes = LeaveService.timeOffTypes
      .filter(prop('enabled'))
      .filter(compose(neq('unpaid_vacation'), prop('type')));
  };

  controller.changeCapValue = function changeCapValue(model) {
    if (model.cap === 0) {
      model.cap = '';
    } else {
      const intCap = Number(model.cap);
      model.cap = isNaN(intCap) ? '' : intCap;
    }

    model.$$changed = true;
  };

  controller.shouldRequestOnSubmit = function shouldRequestOnSubmit() {
    return (
      controller.tabsControl.activeTab.value === 'availability' && AvailabilityUtils.shouldRequest()
    );
  };

  controller.isSubmitDisabled = function isSubmitDisabled() {
    return (
      controller.tabsControl.activeTab.value === 'availability' &&
      !AvailabilityUtils.canSaveSets(controller.availabilitySets)
    );
  };

  controller.addBaseWageRate = function addBaseWageRate() {
    controller.user.baseWages.push({
      fromDate: UserUtils.getWageDefaultDate(controller.user),
      _edited: true,
      userId: controller.user.id,
      _added: -Utils.getNowDate().getTime(),
    });
    controller.form.$setDirty();
  };

  controller.deleteBaseWageRate = function deleteBaseWageRate(wage) {
    wage.deleted = wage._edited = true;
    controller.form.$setDirty();
  };

  controller.wagesGroupHidden = function wagesGroupHidden(wagesGroup) {
    return (
      wagesGroup.hidden || (!wagesGroup.position && !wagesGroup.groupWages.some((w) => !w.deleted))
    );
  };

  controller.loadTimeoffReport = function loadTimeoffReport() {
    _refreshTimeoffReport();
  };

  async function _refreshTimeoffReport() {
    if (!controller.timeoffReportYear || !/^\d{4}$/.test(controller.timeoffReportYear)) {
      return;
    }

    controller.timeoffReportDataLoading = true;
    try {
      const data = await LeaveService.getUserDetailedLeaveReport(
        controller.user.id,
        controller.timeoffReportYear
      );

      if (UserUtils.canAccessUserLeaveCaps(controller.user)) {
        data.caps = await LeaveService.getUserCaps(controller.user.id, { asDict: true });
      }

      controller.timeoffReportData = data;
    } catch (e) {
      console.error(e);
    } finally {
      controller.timeoffReportDataLoading = false;
    }
  }

  controller.getDataForTimeoffReportExport = function getDataForTimeoffReportExport() {
    const exportData = {
      header: ['Type', 'Request', 'Duration', 'Comment', 'Status', 'Paid'],
      body: [],
      footer: [],
    };

    controller.timeoffReportData.leaves.forEach((leave) => {
      exportData.body.push([
        leave.typeName,
        leave.requestFromToText,
        leave.requestDurationText,
        leave.summary || '-',
        leave.statusText,
        leave.paid ? 'Paid' : 'Unpaid',
      ]);
    });

    return exportData;
  };

  controller.getUnavailabilityId = function getUnavailabilityId() {
    return $location.search().unavailabilityId;
  };

  controller.shouldShowUnavailabilityDetailsView = function shouldShowUnavailabilityDetailsView() {
    return !!controller.getUnavailabilityId() || $state.params.newSet;
  };

  controller.setUnavailabilityData = function setUnavailabilityData(data) {
    if (data) {
      this.availabilitySets = Array.isArray(data) ? data : [data];
    }
  };

  function _parseLeaveCaps(leaveCaps) {
    const userCaps = leaveCaps.reduce(
      (agg, cap) => ((agg[cap.leaveTypeId || 'global'] = { ...cap }), agg),
      {}
    );

    const orgCaps = controller.timeOffTypes.reduce(
      (agg, timeOffType) => ((agg[timeOffType.id] = timeOffType.cap), agg),
      {}
    );

    if (Authentication.user.orgSettings.useGlobalLeaveCap) {
      controller.userLeaveCaps = {
        global: userCaps.global || {
          cap: Authentication.user.orgSettings.leaveCap,
          leaveTypeId: null,
        },
      };
    } else {
      controller.userLeaveCaps = Object.entries(orgCaps).reduce((agg, [id, cap]) => {
        agg[id] = id in userCaps ? userCaps[id] : { cap, leaveTypeId: Number(id) };
        return agg;
      }, {});
    }
  }

  async function _getUserCaps(userId) {
    controller._originalUserCaps = await LeaveService.getUserCaps(userId);
    return controller._originalUserCaps;
  }

  async function _saveLeaveCaps() {
    if (!controller.userLeaveCaps) {
      return;
    }

    const payload = Object.values(controller.userLeaveCaps)
      .filter(prop('$$changed'))
      .map((cap) => ({
        ...cap,
        userId: controller.user.id,
        cap: 'cap' in cap ? cap.cap : null,
      }));

    if (payload.length) {
      await LeaveService.updateUserCaps(controller.user, payload);
      _parseLeaveCaps(await _getUserCaps(user.id));

      return true;
    }
  }

  function _addWageToGroup(group) {
    const hasWageForPosition = controller.user.wagesList.some(({ position }) => position === group);
    if (!hasWageForPosition) {
      controller.user.wagesList.push({
        position: group,
        wages: [],
        groupWages: [],
      });
    }
  }

  function _filterEmptyWages() {
    controller.user.wagesList = controller.user.wagesList.filter(
      (wageGroup) =>
        wageGroup.wages.length || controller.user.positionIds.includes(wageGroup.position.id)
    );
  }

  function _addUserBaseWagesToUpdate(list) {
    controller.user.baseWages.forEach((wage) => {
      if (wage._edited && !(!wage.id && wage.deleted)) {
        list.push(wage);
      }
    });
  }

  function _prepareTimeoffReportYearOptions() {
    const { leaveResetType, leaveResetDate } = Authentication.user.orgSettings;
    const currentYear = sdk('timeOffsUtils').getTimeOffYear({
      date: Utils.getNowDate(),
      leaveResetType,
      leaveResetDate,
      hireDate: user.hireDate,
    });

    const yearFrom = sdk('timeOffsUtils').getTimeOffYear({
      date: new Date(Authentication.user.org.createdAt),
      leaveResetType,
      leaveResetDate,
      hireDate: user.hireDate,
    });

    controller.timeoffReportYearOptions = [];
    controller.timeoffReportYear = currentYear;

    for (let year = currentYear + 1; year >= yearFrom; year--) {
      controller.timeoffReportYearOptions.push({
        label: _getYearLabel(
          year,
          leaveResetType === sdk('constants').PTO_FIXED_DATE_TYPE ? leaveResetDate : user.hireDate
        ),
        value: year,
      });
    }
  }

  function _getYearLabel(year, leaveDateString) {
    const resetDate = Utils.getRealDate(leaveDateString);
    resetDate.setFullYear(year);

    const to = Utils.addDays(Utils.addYears(resetDate, 1), -1);
    return `${toDateFilter(resetDate)} - ${toDateFilter(to)}`;
  }

  function getOriginalExtraData() {
    return {
      ...controller.originalUserExtraData,
      managers: (controller.originalUserExtraData.managers || []).map(({ id }) => ({
        id,
        fullname: PersonaCollection.personasDict[id]?.fullname,
      })),
      managedEmployees: controller.originalUserExtraData.managedEmployees?.slice() || [],
    };
  }

  init();
  resetUser();

  const offEventCollectionChange = EventCollection.onChange(() => {
    if (controller.tabsControl.activeTab.value === 'timeoff') {
      Utils.debounce(_refreshTimeoffReport, 1000);
    }
  });

  const offPersonaCollectionChange = PersonaCollection.onChange(() => {
    controller.getCurrentWages();
  });

  const offEventSync = $scope.$on('socket:events:sync', () => {
    if (controller.tabsControl.activeTab.value === 'timeoff') {
      Utils.debounce(_refreshTimeoffReport, 1000);
    }
  });

  $scope.$on('$destroy', () => {
    controller.tabsControl.destroy();
    offEventCollectionChange();
    offPersonaCollectionChange();
    offEventSync();
  });

  $scope.$watchCollection('ctrl.user.locations', controller.refreshManagers);

  if (__TEST__) {
    controller._saveLeaveCaps = _saveLeaveCaps;
    controller._parseLeaveCaps = _parseLeaveCaps;
  }

  controller.sendMessage = (userId) => ConversationService.openConversation(userId);
}
